(ns frontend.view.nev.kyc.process.ubo-editor
    (:require
     [clojure.data :as data]
     [common.country :as country]
     [common.localization :as loc]
     [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
     [frontend.state.current-session :as session]
     [frontend.view.modals.confirmation.view :as confirmation-modal]
     [frontend.view.modals.dynamic.view :as dynamic-modal]
     [frontend.view.modals.events :as modals]
     [frontend.view.nev.kyc.process-status-views.view-utils :as view-utils]
     [frontend.zap-time :as zap-time]
     [reagent.core :as r]
     [ui.components.button.view :as button]
     [ui.components.radix-ui.tooltip.view :as tooltip]
     [ui.util :as uu]))

(defn open-ubo-editor
  [{:keys [idx existing-ubo kyc-entity-editor process-id state-atom lang-kw question-attribute
           question-meta answers-atom mandatory? read-only? save!]}]
  (let [kyc-process (session/get-entity process-id)
        ubo-answers (r/atom (or existing-ubo {}))
        field-errors-atom (r/atom {})
        ubos (get @answers-atom question-attribute)
        ubo-template-schema (:data/template question-meta)
        calc-new-ubo-answer (fn []
                              (if (>= idx (count ubos))
                                (concat ubos
                                        [(assoc @ubo-answers
                                                :ord
                                                (count ubos))])
                                (->> ubos
                                     (map-indexed (fn [uidx ubo]
                                                    (assoc (if (= uidx idx)
                                                             @ubo-answers
                                                             ubo)
                                                           :ord
                                                           uidx)))
                                     (vec))))]
    (modals/open-modal!
      dynamic-modal/modal-key
      {:title               (loc/tr :add-ubo nil lang-kw)
       :render-fn           (fn [_state]
                              [:div
                               [kyc-entity-editor
                                {:container-id    (str "UBO-" idx)
                                 :process-id      process-id
                                 :state-atom      state-atom
                                 :save!           identity
                                 :mandatory?      mandatory?
                                 :skip-info?      true
                                 :read-only?      read-only?
                                 :answers-atom    ubo-answers
                                 :field-errors-atom field-errors-atom
                                 :template-schema ubo-template-schema
                                 :lang-kw         lang-kw}]])
       :show-button?        (not read-only?)
       :button-text         (loc/tr :save nil lang-kw)
       :custom-validator-fn (fn [_state]
                              (let [[old-only new-only _] (data/diff existing-ubo @ubo-answers)
                                    changes? (or (seq old-only)
                                                 (seq new-only))]
                                (view-utils/modal-validator-tooltip
                                 {:lang-kw           lang-kw
                                  :template-schema   ubo-template-schema
                                  :answers-atom      ubo-answers
                                  :field-errors-atom field-errors-atom
                                  :kyc-process       kyc-process
                                  :changes?          changes?})))
       :on-submit!          (fn [_]
                              (let [new-value (calc-new-ubo-answer)]
                                (swap! answers-atom assoc question-attribute new-value)
                                (save!)))
       :on-before-close!    (fn [close!]
                              (if (= (get @answers-atom question-attribute) (calc-new-ubo-answer))
                                (close!)
                                (modals/open-modal!
                                  confirmation-modal/modal-key
                                  {:body           (loc/tr :confirm-close-kyc-modal-without-saving nil lang-kw)
                                   :confirm-text   (loc/tr :close-without-saving nil lang-kw)
                                   :confirm-icon   ""
                                   :skip-checkbox? true
                                   :on-confirm     close!})))})))

(defn view
  [{:keys [process-id lang-kw question-attribute question-meta
           answers-atom mandatory? read-only? save!]
    :as   args}]
  (let [ubos (get @answers-atom question-attribute)
        process (session/get-entity process-id)
        tenant-kyc-process? (-> process
                                :kyc-process/containing-entity-type
                                (= :entity.type/tenant-kyc-profile))
        contract-kyc-process? (-> process
                                  :kyc-process/containing-entity-type
                                  (= :entity.type/contract-kyc-profile))
        ubo-schema (:data/template question-meta)
        wip-status? (-> process
                        :kyc-process/status
                        (= :kyc-process.status/wip))
        sorted-ubos (sort-by :ord ubos)
        ubo-missing-data-indices
        (->> sorted-ubos
             (map-indexed
               (fn [idx ubo]
                 (let [missing-fields (view-utils/resolve-missing-fields lang-kw
                                                                         ubo-schema
                                                                         (atom ubo)
                                                                         process)]
                   [idx missing-fields])))
             (filterv (fn [[_idx missing-fields]]
                        (seq missing-fields)))
             (mapv first)
             set)
        info (get-in question-meta [:info lang-kw])
        info-below-label? (and info
                               (-> question-meta
                                   :info-position
                                   (= :below-label)))]
    [:div
     [:div.input-editor__label
      [:label
       {:class (uu/cm "input-editor__label-text"
                      (if info-below-label?
                        "pb-1" "pb-2")
                      (when mandatory?
                        "after:ml-0.5 after:text-danger-regular after:content-['*']"))}
       (-> question-meta
           :translations
           (get lang-kw))]]

     (when info-below-label?
       [:p
        {:class "pb-2 text-sm font-normal m-0 text-base-light-text-paler"}
        info])

     (if (and read-only?
              (empty? ubos))
       [:div.preview
        [:span (loc/tr :no-ubos)]]
       [:div.max-w-100.hide-scrollbar
        [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
         [:thead.zap-table__headline
          [:tr.zap-table__row
           [:th.zap-table__headline ""]
           [:th.zap-table__headline
            (kyc-utils/localize-question ubo-schema :kyc-ubo/first-name lang-kw)]
           [:th.zap-table__headline
            (kyc-utils/localize-question ubo-schema :kyc-ubo/last-name lang-kw)]
           [:th.zap-table__headline
            (kyc-utils/localize-question ubo-schema :kyc-ubo/country lang-kw)]
           [:th.zap-table__headline (kyc-utils/localize-question ubo-schema :kyc-ubo/pep lang-kw)]
           [:th.zap-table__headline
            (when-not (or tenant-kyc-process?
                          contract-kyc-process?)
              (loc/tr :valid-docs nil lang-kw))]
           [:th.zap-table__headline ""]]]
         [:tbody
          (->>
            sorted-ubos
            (map-indexed
              (fn [idx ubo]
                [:tr
                 {:key (str "UBO-" idx "-" (:ord ubo))}
                 [:td.zap-table__cell
                  [:i.fal.fa-external-link.clickable
                   {:on-click (fn [_]
                                (open-ubo-editor (assoc args
                                                        :idx idx
                                                        :existing-ubo ubo)))}]]
                 [:td.zap-table__cell
                  (:kyc-ubo/first-name ubo)]
                 [:td.zap-table__cell
                  (:kyc-ubo/last-name ubo)]
                 [:td.zap-table__cell
                  (country/get-label (:kyc-ubo/country ubo) lang-kw)]
                 [:td.zap-table__cell
                  (when-let [is-pep-kw (:kyc-ubo/pep ubo)]
                    (kyc-utils/localize-display-string ubo-schema :kyc-ubo/pep is-pep-kw lang-kw))]
                 [:td.zap-table__cell
                  (when-not (or tenant-kyc-process?
                                contract-kyc-process?)
                    (if (< (zap-time/now) (:kyc-ubo/identification-expires ubo))
                      (loc/tr :yes nil lang-kw)
                      (loc/tr :no nil lang-kw)))]
                 [:td.zap-table__cell
                  (when (and wip-status?
                             (seq ubo-missing-data-indices))
                    {:style {:border-right "1px solid #D9D9DA"}})
                  (when-not read-only?
                    [:i.fal.fa-trash-alt.pointer
                     {:on-click (fn [_]
                                  (swap! answers-atom assoc
                                         question-attribute
                                         (concat (take idx ubos)
                                                 (drop (inc idx) ubos)))
                                  (save!))}])]

                 (when (and wip-status?
                            (contains? ubo-missing-data-indices idx))
                   [:td
                    {:class "px-3 py-2"}
                    [tooltip/view
                     [:i {:class "fal fa-exclamation-triangle text-danger-regular"}]
                     [:span (loc/tr :data-missing nil lang-kw)]]])]))
            (doall))]
         (when-not read-only?
           [:tfoot
            [:tr.zap-table__row
             [:td.zap-table__footer-cell {:col-span "2"}
              [button/primary-solid
               {:on-click (fn [_]
                            (let [idx (count ubos)]
                              (open-ubo-editor (assoc args :idx idx))))}
               [:i.fal.fa-plus.pr-2]
               (loc/tr :add-new nil lang-kw)]]]])]])]))

